import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUSES = ["ordered", "received", "inventory", "assigned", "in_repair", "retired",
                  "disposed"];
const WARRANTY = [["", "Any warranty"], ["expired", "Expired"], ["expiring", "Expiring soon"],
                  ["covered", "Covered"], ["unknown", "Not recorded"]];

const WARRANTY_STYLE = { expired: "text-red-800", expiring: "text-amber-800",
                         covered: "text-emerald-800", unknown: "text-muted-foreground" };

export default function Assets() {
  const [data, setData] = useState(null);
  const [spares, setSpares] = useState(null);
  const [orders, setOrders] = useState(null);
  const [issue, setIssue] = useState({ holder_email: "", catalog_item_key: "", note: "" });
  const [receipt, setReceipt] = useState({ catalog_item_key: "", location_key: "", tag: "",
                                            tags: [] });
  const [received, setReceived] = useState(null);
  const [filters, setFilters] = useState({ status: "", location_key: "", warranty: "" });
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ holder_email: "", location_key: "", warranty_expiry: "" });
  const [canWrite, setCanWrite] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    apiFetch(`/assets/lifecycle?${params}`).then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError("You do not have asset access (cmdb.read)."); setData(null); return; }
      setError(null); setData(body);
    });
    apiFetch("/assets/spares").then(async (r) => {
      if (r.ok) setSpares(await r.json());
    });
    apiFetch("/assets/spares/orders").then(async (r) => {
      if (r.ok) setOrders(await r.json());
    });
  }, [filters]);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/auth/my-permissions").then((r) => (r.ok ? r.json() : null)).then((p) => {
      const perms = p?.permissions || [];
      setCanWrite(perms.includes("asset.write"));
    });
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const save = () => {
    const body = {};
    if (form.holder_email) body.holder_email = form.holder_email;
    if (form.location_key) body.location_key = form.location_key;
    if (form.warranty_expiry) body.warranty_expiry = form.warranty_expiry;
    apiFetch(`/assets/${selected.id}/lifecycle`, { method: "POST", body: JSON.stringify(body) })
      .then(async (r) => {
        const out = await r.json();
        if (!r.ok) {
          setStatus(`Update failed — ${typeof out.detail === "string" ? out.detail
                                                                      : JSON.stringify(out.detail)}`);
          return;
        }
        setStatus(`${selected.asset_tag} updated`);
        setForm({ holder_email: "", location_key: "", warranty_expiry: "" });
        setSelected(null);
        load();
      });
  };

  const raiseOrders = async () => {
    const r = await apiFetch("/assets/spares/orders", { method: "POST" });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      setStatus(typeof body.detail === "string" ? body.detail : "Could not raise restock orders");
      return;
    }
    setStatus(`${body.totals.raised_now} restock request(s) raised; `
      + `${body.totals.already_raised} already open.`);
    load();
  };

  const scanTag = () => {
    const tag = receipt.tag.trim();
    if (!tag || receipt.tags.includes(tag)) { setReceipt({ ...receipt, tag: "" }); return; }
    setReceipt({ ...receipt, tags: [...receipt.tags, tag], tag: "" });
  };

  const receiveStock = async () => {
    const r = await apiFetch("/assets/spares/receive", {
      method: "POST",
      body: JSON.stringify({ catalog_item_key: receipt.catalog_item_key,
                             location_key: receipt.location_key || null,
                             tags: receipt.tags }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      setStatus(typeof body.detail === "string" ? body.detail
        : "Could not book those devices in");
      return;
    }
    setReceived(body);
    setStatus(body.message);
    setReceipt({ ...receipt, tag: "", tags: [] });
    load();
  };

  const issueSpare = async (row, faultyAssetId) => {
    const r = await apiFetch("/assets/spares/issue", {
      method: "POST",
      body: JSON.stringify({
        holder_email: issue.holder_email,
        catalog_item_key: row ? row.catalog_item_key : issue.catalog_item_key,
        location_key: row ? row.location_key : null,
        faulty_asset_id: faultyAssetId || null,
        note: issue.note,
      }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = body.detail;
      setStatus(typeof detail === "string" ? detail
        : `${detail?.message || "Could not issue a spare"} ${detail?.what_to_do || ""}`);
      return;
    }
    setStatus(body.message + (body.low_stock ? " Stock here is now below the minimum." : ""));
    setIssue({ holder_email: "", catalog_item_key: "", note: "" });
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="assets-title">Asset Lifecycle</h1>
          <p className="text-sm text-muted-foreground">
            Every device, who holds it, where it lives and how long its warranty has left.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="assets-calendar-link">
            <Link to="/calendar">Calendar</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {error && <p className="text-sm text-destructive" data-testid="assets-error">{error}</p>}
        {status && <p className="text-sm text-amber-800" data-testid="assets-status">{status}</p>}

        <section className="grid gap-3 md:grid-cols-3" data-testid="assets-filters">
          <div className="space-y-1">
            <label htmlFor="assets-status" className="text-sm font-medium">Lifecycle status</label>
            <select id="assets-status" data-testid="assets-status-filter" value={filters.status}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">Any status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="assets-site" className="text-sm font-medium">Site</label>
            <select id="assets-site" data-testid="assets-site-filter" value={filters.location_key}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setFilters({ ...filters, location_key: e.target.value })}>
              <option value="">Every site</option>
              {(data?.sites || []).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="assets-warranty" className="text-sm font-medium">Warranty</label>
            <select id="assets-warranty" data-testid="assets-warranty-filter"
                    value={filters.warranty}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setFilters({ ...filters, warranty: e.target.value })}>
              {WARRANTY.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
        </section>

        {data && (
          <>
            <section className="grid gap-3 md:grid-cols-4 text-sm" data-testid="assets-totals">
              {[["Devices", data.totals.assets, "count"],
                ["Warranty expired", data.totals.expired, "expired"],
                [`Expiring within ${data.warranty_warning_days} days`, data.totals.expiring,
                 "expiring"],
                ["No holder recorded", data.totals.unassigned, "unassigned"]].map(
                ([label, value, id]) => (
                  <div key={id} className="border rounded-md px-3 py-2"
                       data-testid={`assets-total-${id}`}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-semibold">{value}</p>
                  </div>
                ))}
            </section>

            <p className="text-sm text-muted-foreground" data-testid="assets-method">
              {data.definition} Method: {data.method}
            </p>

            <section className="space-y-2" data-testid="assets-table">
              <h2 className="text-base md:text-lg font-medium">Devices</h2>
              <div className="overflow-x-auto" tabIndex={0} role="region"
                   aria-label="Devices with holder, site, status and warranty">
                <table className="w-full text-sm border rounded-md">
                  <caption className="sr-only">
                    Assets with holder, site, status and warranty
                  </caption>
                  <thead>
                    <tr className="text-left border-b">
                      <th scope="col" className="px-3 py-2">Tag</th>
                      <th scope="col" className="px-3 py-2">Device</th>
                      <th scope="col" className="px-3 py-2">Serial</th>
                      <th scope="col" className="px-3 py-2">Status</th>
                      <th scope="col" className="px-3 py-2">Held by</th>
                      <th scope="col" className="px-3 py-2">Site</th>
                      <th scope="col" className="px-3 py-2">Warranty</th>
                      {canWrite && <th scope="col" className="px-3 py-2">Update</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data.assets.map((a) => (
                      <tr key={a.id} className="border-b last:border-0"
                          data-testid={`asset-row-${a.asset_tag}`}>
                        <th scope="row" className="px-3 py-2 font-mono text-left">
                          {a.asset_tag}
                        </th>
                        <td className="px-3 py-2">
                          {[a.manufacturer, a.model].filter(Boolean).join(" ") ||
                            a.catalog_item_key}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{a.serial_number || "—"}</td>
                        <td className="px-3 py-2">{(a.status || "").replace(/_/g, " ")}</td>
                        <td className="px-3 py-2">
                          {a.holder ? (a.holder.name || a.holder.email) : "unassigned"}
                        </td>
                        <td className="px-3 py-2">{a.location_name || "not recorded"}</td>
                        <td className={`px-3 py-2 ${WARRANTY_STYLE[a.warranty.status]}`}
                            data-testid={`asset-warranty-${a.asset_tag}`}>
                          {a.warranty.status === "unknown" ? "not recorded"
                            : a.warranty.status === "expired"
                              ? `expired ${a.warranty_expiry}`
                              : `${a.warranty_expiry} (${a.warranty.days_remaining} days)`}
                        </td>
                        {canWrite && (
                          <td className="px-3 py-2">
                            <Button size="sm" variant="ghost"
                                    data-testid={`asset-edit-${a.asset_tag}`}
                                    onClick={() => { setSelected(a);
                                                     setForm({ holder_email: a.holder?.email || "",
                                                               location_key: a.location_key || "",
                                                               warranty_expiry: a.warranty_expiry || "" }); }}>
                              Edit
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {data.assets.length === 0 && (
                      <tr><td colSpan={canWrite ? 8 : 7}
                              className="px-3 py-2 text-muted-foreground"
                              data-testid="assets-empty">
                        No devices match these filters.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {spares && (
              <section className="space-y-3 border-t pt-6" data-testid="assets-spares">
                <h2 className="text-base md:text-lg font-medium">Spares</h2>
                <p className="text-sm text-muted-foreground" data-testid="spares-method">
                  {spares.method}
                </p>
                <div className="grid gap-3 md:grid-cols-3 text-sm">
                  {[["Models on the shelf", spares.totals.models, "models"],
                    ["Devices available", spares.totals.available, "available"],
                    ["Below minimum", spares.totals.below_minimum, "low"]].map(
                    ([label, value, id]) => (
                      <div key={id} className="border rounded-md px-3 py-2"
                           data-testid={`spares-total-${id}`}>
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="text-2xl font-semibold">{value}</p>
                      </div>
                    ))}
                </div>
                {canWrite && (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <label htmlFor="spare-holder" className="text-sm font-medium">
                        Issue to (work email)
                      </label>
                      <Input id="spare-holder" data-testid="spare-holder-input"
                             value={issue.holder_email}
                             onChange={(e) => setIssue({ ...issue, holder_email: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="spare-note" className="text-sm font-medium">
                        Note (optional)
                      </label>
                      <Input id="spare-note" data-testid="spare-note-input" value={issue.note}
                             onChange={(e) => setIssue({ ...issue, note: e.target.value })} />
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto" tabIndex={0} role="region"
                     aria-label="Spare devices by model and site">
                  <table className="w-full text-sm border rounded-md" data-testid="spares-table">
                    <caption className="sr-only">Spare devices by model and site</caption>
                    <thead>
                      <tr className="text-left border-b">
                        <th scope="col" className="px-3 py-2">Model</th>
                        <th scope="col" className="px-3 py-2">Site</th>
                        <th scope="col" className="px-3 py-2">Available</th>
                        <th scope="col" className="px-3 py-2">Minimum</th>
                        {canWrite && <th scope="col" className="px-3 py-2">Issue</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {spares.rows.map((row) => (
                        <tr key={`${row.catalog_item_key}-${row.location_key}`}
                            className="border-b last:border-0"
                            data-testid={`spares-row-${row.catalog_item_key}-${row.location_key || "nosite"}`}>
                          <th scope="row" className="px-3 py-2 text-left font-medium">
                            {row.model}
                          </th>
                          <td className="px-3 py-2">{row.location_name}</td>
                          <td className={`px-3 py-2 ${row.below_minimum ? "text-amber-800 font-medium" : ""}`}
                              data-testid={`spares-available-${row.catalog_item_key}-${row.location_key || "nosite"}`}>
                            {row.available}{row.below_minimum ? " — below minimum" : ""}
                          </td>
                          <td className="px-3 py-2">
                            {row.minimum_stock}
                            <span className="block text-xs text-muted-foreground">
                              {row.minimum_source}
                            </span>
                          </td>
                          {canWrite && (
                            <td className="px-3 py-2">
                              <Button size="sm" variant="outline"
                                      disabled={!issue.holder_email || row.available === 0}
                                      data-testid={`spares-issue-${row.catalog_item_key}-${row.location_key || "nosite"}`}
                                      onClick={() => issueSpare(row, selected?.id || null)}>
                                {selected ? `Replace ${selected.asset_tag}` : "Issue one"}
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {spares.rows.length === 0 && (
                        <tr><td colSpan={canWrite ? 5 : 4}
                                className="px-3 py-2 text-muted-foreground"
                                data-testid="spares-empty">
                          Nothing is on the shelf. Every device is with somebody or retired.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {canWrite && (
                  <p className="text-xs text-muted-foreground" data-testid="spares-hint">
                    Select a device in the table above first to replace it: the spare goes to the
                    person and the faulty one goes for repair.
                  </p>
                )}

                {orders && canWrite && (
                  <div className="space-y-2 border rounded-md p-3" data-testid="spares-receive">
                    <h3 className="text-sm font-semibold">Book stock in</h3>
                    <p className="text-xs text-muted-foreground"
                       data-testid="spares-receive-method">
                      Scan or type one serial number at a time — a barcode scanner types into the
                      same field. Each one becomes a device on the shelf, and the restock request
                      closes once the shelf is back at its minimum.
                    </p>
                    <div className="grid gap-3 md:grid-cols-3">
                      <div className="space-y-1">
                        <label htmlFor="receive-model" className="text-sm font-medium">Model</label>
                        <select id="receive-model" data-testid="receive-model"
                                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                                value={receipt.catalog_item_key}
                                onChange={(e) => setReceipt({ ...receipt,
                                                              catalog_item_key: e.target.value })}>
                          <option value="">Choose a model</option>
                          {[...new Set([...(orders.rows || []).map((r) => r.catalog_item_key),
                                        ...(spares.rows || []).map((r) => r.catalog_item_key)])]
                            .filter(Boolean).map((key) => (
                              <option key={key} value={key}>{key}</option>
                            ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="receive-site" className="text-sm font-medium">Site</label>
                        <select id="receive-site" data-testid="receive-site"
                                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                                value={receipt.location_key}
                                onChange={(e) => setReceipt({ ...receipt,
                                                              location_key: e.target.value })}>
                          <option value="">No site recorded</option>
                          {(data?.sites || []).map((s) => (
                            <option key={s.key} value={s.key}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="receive-tag" className="text-sm font-medium">
                          Serial number
                        </label>
                        <Input id="receive-tag" data-testid="receive-tag" value={receipt.tag}
                               onChange={(e) => setReceipt({ ...receipt, tag: e.target.value })}
                               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault();
                                                                           scanTag(); } }} />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <Button size="sm" variant="outline" data-testid="receive-add-tag"
                              disabled={!receipt.tag.trim()} onClick={scanTag}>
                        Add this one
                      </Button>
                      <Button size="sm" data-testid="receive-submit"
                              disabled={!receipt.catalog_item_key || receipt.tags.length === 0}
                              onClick={receiveStock}>
                        Book in {receipt.tags.length || 0} device(s)
                      </Button>
                      <span className="text-xs text-muted-foreground"
                            data-testid="receive-scanned">
                        {receipt.tags.length === 0 ? "Nothing scanned yet."
                          : `Scanned: ${receipt.tags.join(", ")}`}
                      </span>
                    </div>
                    {received && (
                      <div className="text-xs space-y-1" data-testid="receive-result">
                        <p data-testid="receive-result-message">{received.message}</p>
                        <p className="text-muted-foreground">
                          {received.available} on the shelf against a minimum of{" "}
                          {received.minimum_stock}.
                        </p>
                        {received.rejected.length > 0 && (
                          <ul className="text-amber-900" data-testid="receive-rejected">
                            {received.rejected.map((r) => (
                              <li key={r.tag}>{r.tag} — {r.why}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {orders && (
                  <div className="space-y-2" data-testid="spares-orders">
                    <h3 className="text-sm font-semibold">Restock orders</h3>
                    <p className="text-xs text-muted-foreground" data-testid="spares-orders-method">
                      {orders.method}
                    </p>
                    <ul className="text-sm space-y-1">
                      {orders.rows.map((row) => (
                        <li key={`${row.catalog_item_key}-${row.location_key}`}
                            data-testid={`spares-order-${row.catalog_item_key}-${row.location_key || "nosite"}`}>
                          {row.label || row.model} at {row.location_key || "the warehouse"} — short{" "}
                          {row.shortfall};{" "}
                          {row.open_request
                            ? `request ${row.open_request.number} is open`
                            : "no request raised yet"}
                        </li>
                      ))}
                      {orders.rows.length === 0 && (
                        <li className="text-muted-foreground" data-testid="spares-orders-empty">
                          Every model is at or above its minimum. Nothing to order.
                        </li>
                      )}
                    </ul>
                    {canWrite && orders.rows.some((r) => !r.open_request) && (
                      <Button size="sm" variant="outline" data-testid="spares-raise-orders"
                              onClick={raiseOrders}>
                        Raise the restock requests
                      </Button>
                    )}
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {selected && canWrite && (
          <section className="space-y-3 border-t pt-6" data-testid="asset-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-medium" data-testid="asset-detail-title">
                {selected.asset_tag} — {[selected.manufacturer, selected.model]
                  .filter(Boolean).join(" ") || selected.catalog_item_key}
              </h2>
              <Button size="sm" variant="ghost" data-testid="asset-detail-close"
                      onClick={() => setSelected(null)}>Close</Button>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <label htmlFor="asset-holder" className="text-sm font-medium">
                  Holder email
                </label>
                <Input id="asset-holder" data-testid="asset-holder-input"
                       value={form.holder_email}
                       onChange={(e) => setForm({ ...form, holder_email: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label htmlFor="asset-location" className="text-sm font-medium">Site</label>
                <select id="asset-location" data-testid="asset-location-input"
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={form.location_key}
                        onChange={(e) => setForm({ ...form, location_key: e.target.value })}>
                  <option value="">Leave unchanged</option>
                  {(data?.sites || []).map((s) => (
                    <option key={s.key} value={s.key}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label htmlFor="asset-warranty" className="text-sm font-medium">
                  Warranty expiry
                </label>
                <Input id="asset-warranty" type="date" data-testid="asset-warranty-input"
                       value={form.warranty_expiry}
                       onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} />
              </div>
            </div>
            <Button size="sm" data-testid="asset-save" onClick={save}>Record it</Button>

            <div className="space-y-1">
              <h3 className="text-sm font-semibold">History</h3>
              <ul className="text-sm space-y-1" data-testid="asset-history">
                {(selected.history || []).map((h, i) => (
                  <li key={i} className="text-muted-foreground">
                    {h.event} — {h.at}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
